import { useState, useEffect } from "react";
import { motion } from "framer-motion";

import Section from "../layout/Section";
import Container from "../layout/Container";

import {
  CalendarCheck,
  Settings,
  TrendingUp,
  BarChart3,
} from "lucide-react";

import { fadeUp, stagger } from "@/utils/motion";

/* 🔥 FALLBACK */
const FALLBACK =
  "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1400";

/* 🔥 IMAGE PRELOADER */
const preloadImages = (steps) => {
  steps.forEach((step) => {
    const img = new Image();
    img.src = step.image;
  });
};

export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const steps = [
    {
      title: "Understand your property",
      desc: "We study your location, demand and positioning to uncover real revenue potential.",
      icon: CalendarCheck,
      image:
        "https://res.cloudinary.com/dge0knh7q/image/upload/f_auto,q_auto,w_1400/Understand_Property_saqagb.png",
    },
    {
      title: "Set up for performance",
      desc: "Listings, pricing and systems are built to maximise visibility and booking intent.",
      icon: Settings,
      image:
        "https://res.cloudinary.com/dge0knh7q/image/upload/f_auto,q_auto,w_1400/Performance_dqajh0.png",
    },
    {
      title: "Operate and optimise",
      desc: "We manage guests, pricing and day to day performance to keep occupancy strong.",
      icon: TrendingUp,
      image:
        "https://res.cloudinary.com/dge0knh7q/image/upload/f_auto,q_auto,w_1400/Operate_optimize_ouzhsy.png",
    },
    {
      title: "Scale consistently",
      desc: "Ongoing insights and optimisation to grow revenue month after month.",
      icon: BarChart3,
      image:
        "https://res.cloudinary.com/dge0knh7q/image/upload/f_auto,q_auto,w_1400/Reporting_sked81.png",
    },
  ];

  /* 🔥 PRELOAD ALL IMAGES ON MOUNT */
  useEffect(() => {
    preloadImages(steps);
  }, []);

  /* 🔥 PRELOAD ACTIVE IMAGE (INSTANT SWITCH) */
  useEffect(() => {
    setLoaded(false);

    const img = new Image();
    img.src = steps[active].image;
    img.onload = () => setLoaded(true);
  }, [active]);

  return (
    <Section
      id="how"
      background="bg-brand-secondary"
      padding="none"
      className="pt-10 pb-16 lg:pt-14 lg:pb-20"
    >
      <Container>

        {/* HEADER */}
        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-content"
        >
          <motion.p
            variants={fadeUp}
            className="text-small font-semibold uppercase tracking-wide text-white/70"
          >
            How it works
          </motion.p>

          <motion.h2
            variants={fadeUp}
            className="mt-2 text-display font-extrabold text-white"
          >
            A system built to grow your property revenue
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="mt-3 text-body text-white/70 max-w-reading"
          >
            From setup to optimisation, every step is designed to increase occupancy and returns without adding complexity.
          </motion.p>
        </motion.div>

        {/* MAIN */}
        <div className="
          mt-10 lg:mt-14
          grid gap-10 lg:gap-12
          lg:grid-cols-[1fr_1.2fr]
          items-start lg:items-center
        ">

          {/* LEFT */}
          <div className="flex flex-col gap-4 lg:gap-5">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = i === active;

              return (
                <motion.div
                  key={i}
                  onClick={() => setActive(i)}
                  whileTap={{ scale: 0.98 }}
                  className={`
                    cursor-pointer rounded-xl p-4 lg:p-5
                    transition-all duration-normal border
                    ${
                      isActive
                        ? "bg-white/10 border-white/20"
                        : "border-white/10 hover:bg-white/5"
                    }
                  `}
                >
                  <div className="flex items-start gap-3 lg:gap-4">

                    <div className="
                      w-10 h-10 lg:w-11 lg:h-11
                      flex items-center justify-center
                      rounded-xl bg-white/10 border border-white/20
                      shrink-0
                    ">
                      <Icon size={18} className="text-white" />
                    </div>

                    <div className="max-w-[30rem]">
                      <div className="text-body font-semibold text-white leading-tight">
                        {step.title}
                      </div>

                      <div className="text-small text-white/70 mt-1.5 leading-relaxed">
                        {step.desc}
                      </div>
                    </div>

                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* RIGHT VISUAL */}
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.28 }}
            className="
              relative w-full
              aspect-[3/2]
              lg:aspect-[4/3]
              rounded-xl overflow-hidden
              border border-white/10
              bg-bg-surface
            "
          >

            {/* SKELETON */}
            {!loaded && (
              <div className="absolute inset-0 bg-white/10 animate-pulse" />
            )}

            {/* IMAGE */}
            <img
              src={steps[active].image}
              alt={steps[active].title}
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.currentTarget.src = FALLBACK;
              }}
              className={`
                absolute inset-0 w-full h-full
                object-cover object-center
                transition-opacity duration-300
                ${loaded ? "opacity-100" : "opacity-0"}
              `}
            />

            {/* OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

            {/* CONTENT */}
            <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 text-white">
              <div className="text-body font-semibold">
                {steps[active].title}
              </div>
            </div>

          </motion.div>

        </div>

      </Container>
    </Section>
  );
}